import React, { useEffect, useMemo, useState } from "react";
import { api, fmtCompact, fmtUSD } from "../lib/api";

const COLOR_DIRECT_BASE   = [15, 23, 42];   // slate-950 — direct = deeper
const COLOR_INDIRECT_BASE = [148, 163, 184]; // slate-400 — indirect = lighter

export default function DirectIndirectView({ summary }) {
  const [segments, setSegments] = useState(null);
  const [drivers, setDrivers] = useState({});
  const [filter, setFilter] = useState("ALL");
  const [hover, setHover] = useState(null);
  const [box, setBox] = useState({ w: 900, h: 400 });

  useEffect(() => {
    api.segments().then(setSegments).catch(() => setSegments([]));
    api.marketSegmentDrivers()
      .then((d) => setDrivers(Object.fromEntries((d.segments || []).map((s) => [s.segment_code, s]))))
      .catch(() => setDrivers({}));
  }, []);

  if (!segments || !summary) {
    return <div data-testid="direct-indirect-loading" className="bg-slate-900 border border-slate-800 h-[420px] p-6 skeleton" />;
  }

  const filtered = segments.filter((s) =>
    (filter === "ALL" || s.category === filter) && s.realized > 0
  );

  const directRealized = summary.direct.realized;
  const indirectRealized = summary.indirect.realized;
  const total = directRealized + indirectRealized;
  const directPct = total ? (directRealized / total) * 100 : 0;

  return (
    <section
      data-testid="direct-indirect-view"
      className="bg-slate-900 border border-slate-800 flex flex-col"
    >
      <header className="px-5 py-2.5 border-b border-slate-800 flex items-center justify-between">
        <div>
          <h2 className="font-display text-sm font-semibold text-slate-50">
            Direct vs Indirect · 72 UNSPSC Segments
          </h2>
          <p className="text-[11px] text-slate-500 mt-0.5 font-mono">
            Enterprise spend · realized award value · click a tile for detail
          </p>
        </div>
        <div className="flex items-center gap-1 bg-slate-950 border border-slate-800 p-0.5 rounded-sm" data-testid="di-filter">
          {["ALL", "DIRECT", "INDIRECT"].map((opt) => (
            <button
              key={opt}
              onClick={() => setFilter(opt)}
              data-testid={`di-filter-${opt.toLowerCase()}`}
              className={`px-3 py-1 text-[11px] tracking-mono-label rounded-sm transition-colors ${
                filter === opt
                  ? "bg-emerald-500/20 text-emerald-300"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3">
        <div className="lg:col-span-2 p-3 border-b lg:border-b-0 lg:border-r border-slate-800 h-[400px] relative">
          <SquarifiedTreemap
            data={filtered}
            onSizeChange={setBox}
            onHover={setHover}
          />
          {hover && (
            <div
              className="absolute z-10 bg-slate-950 border border-slate-700 px-3 py-2 text-xs shadow-2xl pointer-events-none min-w-[240px]"
              style={{
                left: Math.min(hover.x + 8, box.w - 260),
                top:  Math.min(hover.y + 8, box.h - 110),
              }}
              data-testid="treemap-tooltip"
            >
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ background: hover.category === "DIRECT" ? `rgb(${COLOR_DIRECT_BASE.join(",")})` : `rgb(${COLOR_INDIRECT_BASE.join(",")})` }}
                />
                <span className="font-mono text-slate-500 text-[10px]">
                  {hover.segment_code} · {hover.category}
                </span>
              </div>
              <div className="text-slate-50 font-semibold leading-snug mb-2 break-words max-w-[260px]">
                {hover.segment_name}
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] font-mono">
                <span className="text-slate-500">Total Spend</span>
                <span className="text-emerald-400 text-right">{fmtUSD(hover.realized)}</span>
                <span className="text-slate-500">Baseline</span>
                <span className="text-slate-400 text-right">{fmtUSD(hover.baseline)}</span>
                <span className="text-slate-500">Suppliers</span>
                <span className="text-sky-300 text-right">{hover.supplier_count ?? 0}</span>
                <span className="text-slate-500">POs</span>
                <span className="text-slate-300 text-right">{hover.po_count}</span>
              </div>
              {drivers[hover.segment_code]?.primary && (
                <div className="mt-2 pt-2 border-t border-slate-800" data-testid="treemap-tooltip-driver">
                  <div className="tracking-mono-label text-[9px] text-slate-500 mb-1">
                    Primary Driver
                  </div>
                  <DriverInline d={drivers[hover.segment_code].primary} />
                  {drivers[hover.segment_code].secondary && (
                    <div className="mt-1">
                      <span className="tracking-mono-label text-[9px] text-slate-600">SECONDARY</span>
                      <DriverInline d={drivers[hover.segment_code].secondary} muted />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <aside className="p-5 space-y-5">
          <SplitBar
            label="DIRECT"
            value={directRealized}
            pct={directPct}
            color={`rgb(${COLOR_DIRECT_BASE.join(",")})`}
            poCount={summary.direct.po_count}
            supCount={summary.direct.supplier_count}
          />
          <SplitBar
            label="INDIRECT"
            value={indirectRealized}
            pct={100 - directPct}
            color={`rgb(${COLOR_INDIRECT_BASE.join(",")})`}
            poCount={summary.indirect.po_count}
            supCount={summary.indirect.supplier_count}
          />
          <div className="pt-4 border-t border-slate-800">
            <div className="tracking-mono-label text-[10px] text-slate-500 mb-2">
              Top Segments (Realized)
            </div>
            <ul className="space-y-2 text-xs">
              {filtered.slice(0, 5).map((s) => (
                <li key={s.segment_code} className="flex items-baseline gap-2" data-testid={`top-seg-${s.segment_code}`}>
                  <span
                    className="h-2 w-2 rounded-full mt-1.5 shrink-0"
                    style={{ background: s.category === "DIRECT" ? `rgb(${COLOR_DIRECT_BASE.join(",")})` : `rgb(${COLOR_INDIRECT_BASE.join(",")})` }}
                  />
                  <span className="text-slate-300 flex-1 truncate">{s.segment_name}</span>
                  <span className="font-mono text-slate-200">{fmtCompact(s.realized)}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </section>
  );
}

function SplitBar({ label, value, pct, color, poCount, supCount }) {
  return (
    <div data-testid={`split-${label.toLowerCase()}`}>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="tracking-mono-label text-[10px] text-slate-400">{label}</span>
        <span className="font-mono text-[11px] text-slate-500">
          {poCount} PO · {supCount} suppliers
        </span>
      </div>
      <div className="font-mono text-2xl font-semibold text-slate-50">{fmtUSD(value)}</div>
      <div className="font-mono text-xs text-slate-400 mt-0.5">{pct.toFixed(2)}% of spend</div>
      <div className="mt-2 h-1.5 bg-slate-800 overflow-hidden rounded-sm">
        <div className="h-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

// ─── Hand-rolled squarified treemap (deterministic, hover-aware) ────────────
function SquarifiedTreemap({ data, onSizeChange, onHover }) {
  const ref = React.useRef(null);
  const [size, setSize] = useState({ w: 900, h: 400 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setSize({ w: Math.max(300, r.width), h: Math.max(280, r.height) });
      onSizeChange({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [onSizeChange]);

  const rects = useMemo(() => squarify(data, size.w, size.h), [data, size]);
  const maxRealized = useMemo(() => Math.max(...data.map((d) => d.realized), 1), [data]);

  return (
    <div ref={ref} className="absolute inset-3" data-testid="unspsc-treemap">
      <svg width="100%" height="100%" viewBox={`0 0 ${size.w} ${size.h}`} preserveAspectRatio="none">
        {rects.map((r) => {
          const base = r.d.category === "DIRECT" ? COLOR_DIRECT_BASE : COLOR_INDIRECT_BASE;
          const opacity = 0.32 + 0.55 * (r.d.realized / maxRealized);
          // Adaptive font sizing — area-aware. Larger tiles get larger fonts.
          const area = r.w * r.h;
          const titleSize = Math.max(9, Math.min(18, Math.round(Math.sqrt(area) / 9)));
          const codeSize  = Math.max(8, Math.min(11, titleSize - 2));
          const valueSize = Math.max(9, Math.min(15, titleSize - 1));
          // How many lines/chars of the segment name we can fit
          const maxChars  = Math.max(6, Math.floor((r.w - 10) / (titleSize * 0.52)));
          const maxLines  = Math.max(1, Math.floor((r.h - 28) / (titleSize + 2)));
          const lines     = wrapText(r.d.segment_name, maxChars, maxLines);
          const showCode  = r.h > 22 && r.w > 36;
          const showName  = r.w > 44 && r.h > 38 && lines.length > 0;
          const showValue = r.w > 50 && r.h > 26;
          return (
            <g
              key={r.d.segment_code}
              data-testid={`treemap-tile-${r.d.segment_code}`}
              onMouseEnter={() => onHover({ ...r.d, x: r.x + r.w / 2, y: r.y })}
              onMouseLeave={() => onHover(null)}
              style={{ cursor: "pointer" }}
            >
              <rect
                x={r.x}
                y={r.y}
                width={Math.max(r.w - 1, 0)}
                height={Math.max(r.h - 1, 0)}
                fill={`rgb(${base.join(",")})`}
                fillOpacity={opacity}
                stroke="#020617"
                strokeWidth={1}
              />
              {showCode && false && (
                <text
                  x={r.x + 6}
                  y={r.y + codeSize + 4}
                  fontFamily="IBM Plex Mono"
                  fontSize={codeSize}
                  fill="#cbd5e1"
                  fillOpacity={0.85}
                >
                  {r.d.segment_code}
                </text>
              )}
              {showName && lines.map((ln, i) => (
                <text
                  key={i}
                  x={r.x + 6}
                  y={r.y + 14 + titleSize + i * (titleSize + 2)}
                  fontFamily="Inter"
                  fontSize={titleSize}
                  fontWeight={600}
                  fill={r.d.category === "DIRECT" ? "#f8fafc" : "#0f172a"}
                >
                  {ln}
                </text>
              ))}
              {showValue && (
                <text
                  x={r.x + 6}
                  y={r.y + r.h - 6}
                  fontFamily="IBM Plex Mono"
                  fontSize={valueSize}
                  fontWeight={700}
                  fill={r.d.category === "DIRECT" ? "#e2e8f0" : "#0f172a"}
                >
                  {fmtCompact(r.d.realized)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function clip(s, max) {
  if (!s) return "";
  return s.length > max ? s.slice(0, Math.max(max - 1, 4)) + "…" : s;
}

// ─── Primary-driver inline chip (used in the treemap tooltip) ───────────────
const SRC_FG = { FRED: "text-amber-300", BLS: "text-sky-300", ECB: "text-violet-300" };
function DriverInline({ d, muted = false }) {
  const arrow = d.trend === "falling" ? "▼" : d.trend === "rising" ? "▲" : "·";
  const colorMom = d.trend === "falling" ? "text-rose-300" : d.trend === "rising" ? "text-emerald-300" : "text-slate-400";
  return (
    <div className="flex items-center gap-2 text-[10px] font-mono" data-testid={`driver-${d.code}`}>
      <span className={`${SRC_FG[d.source] || "text-slate-300"} ${muted ? "opacity-70" : ""}`}>
        {d.source} {d.code}
      </span>
      <span className={`text-slate-400 truncate ${muted ? "opacity-70" : ""}`}>{d.label}</span>
      <span className={`ml-auto ${colorMom}`}>
        {arrow} {(d.mom_pct ?? 0) >= 0 ? "+" : ""}{(d.mom_pct ?? 0).toFixed(2)}%
      </span>
    </div>
  );
}

// Word-wrap into up to `maxLines` lines of ≤ `maxChars` each.
function wrapText(text, maxChars, maxLines) {
  if (!text || maxLines <= 0 || maxChars <= 0) return [];
  const words = text.split(/\s+/);
  const lines = [];
  let current = "";
  for (const w of words) {
    const next = current ? current + " " + w : w;
    if (next.length <= maxChars) {
      current = next;
    } else {
      if (current) lines.push(current);
      if (lines.length >= maxLines) break;
      // word itself longer than maxChars → truncate it
      current = w.length > maxChars ? w.slice(0, Math.max(maxChars - 1, 3)) + "…" : w;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  // If we ran out of room mid-text, suffix the last visible line with ellipsis
  if (lines.length === maxLines) {
    const joined = lines.join(" ");
    const remainder = text.slice(joined.length).trim();
    if (remainder) {
      const last = lines[lines.length - 1];
      lines[lines.length - 1] = last.length >= maxChars - 1
        ? last.slice(0, Math.max(maxChars - 1, 3)) + "…"
        : last + "…";
    }
  }
  return lines;
}

// Squarified treemap layout — Bruls/Huijgens/van Wijk
function squarify(items, w, h) {
  const data = items.filter((d) => d.realized > 0).slice(0, 80);
  const totalValue = data.reduce((a, b) => a + b.realized, 0);
  if (totalValue <= 0 || data.length === 0) return [];

  // Scale values to area
  const scale = (w * h) / totalValue;
  const nodes = data.map((d) => ({ d, v: d.realized * scale }));

  const rects = [];
  let rect = { x: 0, y: 0, w, h };
  let remaining = nodes.slice();

  while (remaining.length) {
    const row = [];
    let bestRatio = Infinity;
    const shortSide = Math.min(rect.w, rect.h);
    while (remaining.length) {
      const candidate = [...row, remaining[0]];
      const ratio = worstRatio(candidate, shortSide);
      if (ratio <= bestRatio) {
        bestRatio = ratio;
        row.push(remaining.shift());
      } else break;
    }
    layoutRow(row, rect, rects);
    rect = shrinkRect(rect, row);
    if (rect.w <= 0 || rect.h <= 0) break;
  }
  return rects;
}

function worstRatio(row, side) {
  if (!row.length) return Infinity;
  const sum = row.reduce((a, b) => a + b.v, 0);
  const sumSq = side * side;
  let worst = 0;
  for (const r of row) {
    const ratio = Math.max((sumSq * r.v) / (sum * sum), (sum * sum) / (sumSq * r.v));
    if (ratio > worst) worst = ratio;
  }
  return worst;
}

function layoutRow(row, rect, out) {
  const sum = row.reduce((a, b) => a + b.v, 0);
  if (rect.w >= rect.h) {
    const rw = sum / rect.h;
    let y = rect.y;
    for (const r of row) {
      const rh = r.v / rw;
      out.push({ x: rect.x, y, w: rw, h: rh, d: r.d });
      y += rh;
    }
  } else {
    const rh = sum / rect.w;
    let x = rect.x;
    for (const r of row) {
      const rw = r.v / rh;
      out.push({ x, y: rect.y, w: rw, h: rh, d: r.d });
      x += rw;
    }
  }
}

function shrinkRect(rect, row) {
  const sum = row.reduce((a, b) => a + b.v, 0);
  if (rect.w >= rect.h) {
    const used = sum / rect.h;
    return { x: rect.x + used, y: rect.y, w: rect.w - used, h: rect.h };
  }
  const used = sum / rect.w;
  return { x: rect.x, y: rect.y + used, w: rect.w, h: rect.h - used };
}
